'use client';

import { useId, useState } from 'react';
import { cn } from '@/lib/utils/tw-merge';
import { UiIcon } from '@/components/ui/UiIcon';
import type { BlockFaqItem } from '@/lib/sanity/dto';

export type FaqAccordionProps = {
    items: BlockFaqItem[];
};

/** Single-open accordion: opening one item closes the previously open one.
 *  The chevron rotates and the answer panel expands via animated transitions
 *  (grid-template-rows 0fr → 1fr), respecting prefers-reduced-motion. */
export function FaqAccordion({ items }: FaqAccordionProps) {
    const baseId = useId();
    const [openKey, setOpenKey] = useState<string | null>(null);

    const toggle = (key: string) => setOpenKey((prev) => (prev === key ? null : key));

    return (
        <div>
            {items.map((item, i) => {
                const key = item._key ?? String(i);
                const isOpen = openKey === key;
                const buttonId = `${baseId}-q-${key}`;
                const panelId = `${baseId}-a-${key}`;

                return (
                    <div key={key} className="border-t border-hairline last:border-b">
                        <button
                            type="button"
                            id={buttonId}
                            aria-expanded={isOpen}
                            aria-controls={panelId}
                            onClick={() => toggle(key)}
                            className="flex w-full cursor-pointer items-center justify-between gap-4 py-5 text-left text-[length:var(--fs-faq-question)] font-medium text-title"
                        >
                            {item.question}
                            <span
                                className={cn(
                                    'shrink-0 text-copy-muted transition-[transform,color] duration-300 ease-out motion-reduce:transition-none',
                                    isOpen && 'rotate-180 text-primary',
                                )}
                            >
                                <UiIcon name="chevron-down" size={20} />
                            </span>
                        </button>
                        <div
                            id={panelId}
                            role="region"
                            aria-labelledby={buttonId}
                            className={cn(
                                'grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
                                isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                            )}
                        >
                            <div className="overflow-hidden">
                                {item.answer ? (
                                    <p className="pb-6 text-md leading-relaxed text-copy-muted">{item.answer}</p>
                                ) : null}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
